<template>
  <div class="flex flex-col gap-10">
    <div v-if="dashboardData" class="flex flex-col">
      <div class="flex justify-between gap-5 mt-10 admin-data-flex-col">
        <div
          v-for="data in firstRowCategories"
          :key="data.name"
          class="w-1/3 flex justify-between gap-20 full-width"
        >
          <div class="information-section full-width w-full">
            <p class="information-number">{{ dashboardData[data.name] }}</p>
            <p class="information-text">{{ $t(`${data.title}`) }}</p>
          </div>
        </div>
        <div class="information-section full-width w-1/3">
          <img
            v-if="dashboardData.companyMustHaveUsers && dashboardData.companyMustHaveUsers.logo"
            :src="resizer(dashboardData.companyMustHaveUsers.logo)"
            alt=""
            class="w-40 h-40 rounded-full"
          />
          <div class="flex flex-col justify-center items-center">
            <p
              class="truncate text-center w-102 font-semibold"
              v-if="dashboardData.companyMustHaveUsers && dashboardData.companyMustHaveUsers.name"
              :title="dashboardData.companyMustHaveUsers.name"
              dir="auto"
            >
              {{ dashboardData.companyMustHaveUsers.name }}
            </p>
            <p class="information-text">{{ $t(`most_members`) }}</p>
          </div>
        </div>
        <div class="information-section full-width w-1/3">
          <img
            v-if="dashboardData.companyMustHaveDevices && dashboardData.companyMustHaveDevices.logo"
            :src="resizer(dashboardData.companyMustHaveDevices.logo)"
            alt=""
            class="w-40 h-40 rounded-full"
          />
          <div class="flex flex-col justify-center items-center">
            <p
              class="truncate text-center w-102 font-semibold"
              v-if="
                dashboardData.companyMustHaveDevices && dashboardData.companyMustHaveDevices.name
              "
              dir="auto"
              :title="dashboardData.companyMustHaveDevices.name"
            >
              {{ dashboardData.companyMustHaveDevices.name }}
            </p>
            <p class="information-text">{{ $t(`most_devices`) }}</p>
          </div>
        </div>
      </div>
      <div class="flex justify-between gap-5 mt-10 admin-data-flex-col">
        <div
          v-for="category in secondRowCategories"
          :key="category"
          class="w-1/2 flex justify-between gap-20 full-width"
        >
          <div class="information-section full-width w-full">
            <p class="information-number">{{ dashboardData[category] }}</p>
            <p class="information-text">{{ $t(category) }}</p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="chartData" class="mt-10">
      <div class="border border-default rounded-sm p-5">
        <div class="flex justify-start">
          <p class="px-20 py-10 font-semibold text-title text-18">
            {{ $t(`members`) }}
          </p>
        </div>
        <div class="mt-20 p-5">
          <base-chart
            v-if="chartData.userData && chartData.userData.yAxis && chartData.userData.xAxis"
            :chartDataProp="chartData.userData.yAxis"
            :chartLabels="chartData.userData.xAxis"
          />
        </div>
      </div>
      <div class="flex gap-20 mt-20 admin-data-flex-col">
        <div class="p-5 w-1/2 border border-default rounded-sm full-width">
          <p class="px-20 py-10 font-semibold text-title text-18">
            {{ $t(`meeting`) }}
          </p>
          <base-chart
            v-if="
              chartData.reservationData &&
              chartData.reservationData.yAxis &&
              chartData.reservationData.xAxis
            "
            :chartDataProp="chartData.reservationData.yAxis"
            :chartLabels="chartData.reservationData.xAxis"
          />
        </div>
        <div class="p-5 w-1/2 border border-default rounded-sm full-width">
          <p class="px-20 py-10 font-semibold text-title text-18">
            {{ $t(`reminder`) }}
          </p>
          <base-chart
            v-if="chartData.snoozeData && chartData.snoozeData.yAxis && chartData.snoozeData.xAxis"
            :chartDataProp="chartData.snoozeData.yAxis"
            :chartLabels="chartData.snoozeData.xAxis"
          />
        </div>
      </div>
    </div>
    <div v-if="tableItems">
      <Table type="meeting" :fields="tableFields" :items="tableItems" />
      <div class="flex justify-center items-center my-50">
        <base-paginate
          :links="links"
          @onPageChange="onPageChange"
          type="meeting"
          v-if="links.current_page"
        />
        <p class="font-bold mx-20">{{ $t('count') }} : {{ links.total }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axiosConfig from '@/services/axios.config';
import BaseChart from '@/components/base/BaseChart.vue';
import imageResizer from '@/utils/image-resizer';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import Table from '@/components/admin/dashboard/Table.vue';
import BasePaginate from '@/components/base/BasePaginate.vue';

const moment = require('moment-jalaali');

export default {
  name: 'AdminData',
  components: {
    BaseChart,
    Table,
    BasePaginate,
  },
  data() {
    return {
      currentPage: 1,
      links: {},
      dashboardData: [],
      chartData: {},
      timeInterval: null,
      imageSize: 150,
      tableFields: [
        'id',
        'created_at',
        'meeting_name',
        'start',
        'end',
        'avatar',
        'organizer_name',
        'organizer_job_position',
        'organizer_id',
        'logo',
        'company_name',
        'company_id',
        'device_name',
        'device_id',
      ],
      tableItems: [],
      secondRowCategories: [
        'total_notification_reminders',
        'total_sms_reminders',
        'count_of_notification_reminders_today',
        'count_of_sms_reminders_today',
        'total_of_events',
        'total_of_events_today',
        'reservation_average_daily',
        'reservation_average_weekly',
      ],
      firstRowCategories: [
        {
          name: 'companies',
          title: 'all_companies',
        },
        {
          name: 'users',
          title: 'members',
        },
        {
          name: 'userOnlines',
          title: 'online_members',
        },
        {
          name: 'devices',
          title: 'devices',
        },
        {
          name: 'onlineDevices',
          title: 'online_devices',
        },
      ],
    };
  },
  created() {
    this.getLatestReservations();
    this.getDashboardData();
    this.getChartData();
    this.startGetDataInterVals();
  },
  beforeDestroy() {
    if (this.timeInterval) clearInterval(this.timeInterval);
  },
  methods: {
    resizer(image) {
      return imageResizer(image, this.imageSize);
    },
    startGetDataInterVals() {
      if (this.timeInterval === null) {
        this.timeInterval = setInterval(() => {
          this.getLatestReservations();
          this.getDashboardData();
          this.getChartData();
        }, 5000);
      }
    },
    onPageChange(currentPageCount) {
      this.getLatestReservations();
      this.currentPage = currentPageCount;
    },
    changeDateFormat(date) {
      const user = JSON.parse(localStorage.getItem('user'));
      const calendar = user.calendar_type;
      if (calendar === 'Solar') {
        moment.locale('fa');
        return moment(date).format('jYYYY-jMM-jDD HH:mm');
        // eslint-disable-next-line no-else-return
      } else {
        moment.locale('en');
        return moment(date).format('YYYY-MM-DD HH:mm');
      }
    },
    getLatestReservations() {
      axiosConfig
        .get(`/admin/dashboard-latest-reservation?per_page=10&page=${this.currentPage}`)
        .then((response) => {
          this.links = response.data.meta;
          // eslint-disable-next-line arrow-body-style
          this.tableItems = response.data.data.map((item) => {
            return {
              id: item?.id,
              created_at: this.changeDateFormat(item?.created_at),
              meeting_name: item?.meeting_name,
              start: this.changeDateFormat(item?.meeting_started_at),
              end: this.changeDateFormat(item?.meeting_ended_at),
              avatar: item.organizer_detail?.avatar,
              organizer_name: item.organizer_detail?.name,
              organizer_job_position: item.organizer_detail?.job_position,
              organizer_id: item.organizer_detail?.id,
              logo: item?.company_logo,
              company_name: item?.company_name,
              company_id: item?.company_id,
              device_name: item?.device_name,
              device_id: item?.device_id,
            };
          });
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
    },
    getDashboardData() {
      axiosConfig
        .get('/admin/dashboard')
        .then((response) => {
          this.dashboardData = response.data.data;
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
    },
    getChartData() {
      axiosConfig
        .get('/admin/chart')
        .then((response) => {
          this.chartData = response.data.data;
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
    },
  },
};
</script>

<style>
@media (max-width: 1024px) {
  .admin-data-flex-col {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .full-width {
    width: 100% !important;
  }
}
.information-section {
  @apply border-1 border-default rounded-sm h-full py-10 flex flex-col justify-center items-center;
}
.information-text {
  @apply text-subtitle font-medium text-16 mt-5 text-center;
}
.information-number {
  @apply font-semibold text-24 mt-20;
}
</style>
